"use client";

import axios from "axios";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import GroupedContainer from "../../Components/GroupedContainer";
import InfoSection from "../../Components/InfoSection";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import PrimaryButton from "../../Components/PrimaryButton";
import SecondaryButton from "../../Components/SecondaryButton";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "../../Components/ui/dialog";
import fallbackImage from "../../Assets/img-profile-error.png";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";

import {
  TbPlus,
  TbEye,
  TbFilter,
  TbSearch,
  TbX,
  TbChevronDown,
  TbInfoCircle,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Employees() {
  const API_BASE = "https://new.cajuu.app/api/users";
  const API_ORIGIN = "https://new.cajuu.app";

  const sigCanvas = useRef(null);
  const clearSignature = () => sigCanvas.current?.clear?.();

  // ✅ list
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ pagination
  const [perPage, setPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    from: 0,
    to: 0,
    per_page: 20,
  });

  // ✅ universal search
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // ✅ sorting
  const [sortBy, setSortBy] = useState("first_name");
  const [sortDirection, setSortDirection] = useState("asc");

  // ✅ modal states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [data, setData] = useState({
    id: null,
    firstName: "",
    lastName: "",
    phone: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
    trailerType: "",
  });

  const [inviteData, setInviteData] = useState({
    email: "",
    role: "",
  });

  // ✅ Drivers map (to show factoring switch even though Employees endpoint returns users)
  const [driversByUserId, setDriversByUserId] = useState({});
  const [driversById, setDriversById] = useState({});
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [updatingFactoringDriverId, setUpdatingFactoringDriverId] = useState(null);

  const FACTORING_TOOLTIP = useMemo(() => {
    return (
      "Factoring enabled here means this Driver can request early payment.\n" +
      "The Driver still must actively consent during Pickup Inspection after seeing the 5% discount.\n" +
      "Once consented, payment can be advanced within the next 2h (subject to inspection requirements)."
    );
  }, []);

  const extractErrorMessage = (error) => {
    try {
      const resData = error?.response?.data;

      if (resData?.errors && typeof resData.errors === "object") {
        const firstKey = Object.keys(resData.errors)[0];
        const firstMsg = Array.isArray(resData.errors[firstKey])
          ? resData.errors[firstKey][0]
          : resData.errors[firstKey];
        if (firstMsg) return String(firstMsg);
      }

      if (resData?.message) return String(resData.message);
      if (typeof resData?.data === "string") return String(resData.data);

      return "An error occurred. Please try again later.";
    } catch (e) {
      return "An error occurred. Please try again later.";
    }
  };

  // ✅ debounce search (same as Shippers)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 450);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getUserRoleName = (roleId) => {
    const roles = [
      { id: 1, user_role_name: "Admin" },
      { id: 2, user_role_name: "Driver" },
      { id: 3, user_role_name: "Customer" },
      { id: 4, user_role_name: "Carrier Company Owner" },
      { id: 5, user_role_name: "Broker Company Owner" },
      { id: 6, user_role_name: "Shipper Company Owner" },
      { id: 7, user_role_name: "Dispatcher" },
      { id: 8, user_role_name: "Dealer" },
      { id: 9, user_role_name: "Accountant" },
    ];

    const role = roles.find((r) => r.id === roleId);
    return role ? role.user_role_name : "Unknown";
  };

  const getUserRoleId = (roleName) => {
    const roles = [
      { id: 1, user_role_name: "Admin" },
      { id: 2, user_role_name: "Driver" },
      { id: 3, user_role_name: "Customer" },
      { id: 4, user_role_name: "Carrier Company Owner" },
      { id: 5, user_role_name: "Broker Company Owner" },
      { id: 6, user_role_name: "Shipper Company Owner" },
      { id: 7, user_role_name: "Dispatcher" },
      { id: 8, user_role_name: "Dealer" },
      { id: 9, user_role_name: "Accountant" },
    ];

    const role = roles.find((r) => r.user_role_name === roleName);
    return role ? role.id : 0;
  };

  const fetchEmployees = useCallback(
    async (pageToFetch = currentPage) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");

        const params = {
          page: pageToFetch,
          per_page: perPage,
          sort_by: sortBy,
          sort_direction: sortDirection,
        };

        const q = (debouncedSearchQuery || "").trim();
        if (q) params.q = q;

        const response = await axios.get(`${API_BASE}/paginated`, {
          params,
          headers: {
            "Screen-Name": "/User",
            Authorization: `Bearer ${token}`,
          },
        });

        const result = response.data;
        const paginator = result?.data || {};
        const list = Array.isArray(paginator?.data) ? paginator.data : [];

        const apiLastPage = parseInt(paginator?.last_page || 1);
        const apiCurrentPage = parseInt(paginator?.current_page || pageToFetch);

        setTotalPages(apiLastPage || 1);
        setPaginationMeta({
          total: parseInt(paginator?.total || 0),
          from: parseInt(paginator?.from || 0) || 0,
          to: parseInt(paginator?.to || 0) || 0,
          per_page: parseInt(paginator?.per_page || perPage) || perPage,
        });

        if (apiLastPage > 0 && pageToFetch > apiLastPage) {
          setCurrentPage(apiLastPage);
        } else {
          setCurrentPage(apiCurrentPage);
        }

        setEmployees(list);
      } catch (error) {
        setEmployees([]);
        setTotalPages(1);
        setPaginationMeta({ total: 0, from: 0, to: 0, per_page: perPage });
        toast.error(extractErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [API_BASE, currentPage, debouncedSearchQuery, perPage, sortBy, sortDirection]
  );

  const fetchDriversForCompany = useCallback(async () => {
    setLoadingDrivers(true);
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`${API_ORIGIN}/api/driver`, {
        headers: {
          "Screen-Name": "/User",
          Authorization: `Bearer ${token}`,
        },
      });

      const list =
        (Array.isArray(res?.data?.data) && res.data.data) ||
        (Array.isArray(res?.data?.data?.data) && res.data.data.data) ||
        (Array.isArray(res?.data) && res.data) ||
        [];

      const byUserId = {};
      const byId = {};

      list.forEach((d) => {
        if (!d || !d.id) return;
        const did = String(d.id);
        byId[did] = d;

        const uid = d.user_id ?? d.user?.id ?? null;
        if (uid) {
          byUserId[String(uid)] = d;
        }
      });

      setDriversByUserId(byUserId);
      setDriversById(byId);
    } catch (error) {
      setDriversByUserId({});
      setDriversById({});
    } finally {
      setLoadingDrivers(false);
    }
  }, [API_ORIGIN]);

  // ✅ refetch on filters/page change (same as Shippers)
  useEffect(() => {
    fetchEmployees(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchQuery, perPage, sortBy, sortDirection]);

  // ✅ drivers map (for factoring switch)
  useEffect(() => {
    fetchDriversForCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setData({
      id: null,
      firstName: "",
      lastName: "",
      phone: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "",
      trailerType: "",
    });
    try {
      clearSignature();
    } catch (e) {}
  };

  const validateCreate = () => {
    const requiredBase = ["role", "firstName", "lastName", "phone", "username", "email", "password", "confirmPassword"];
    const hasEmpty = requiredBase.some((k) => !data[k] || String(data[k]).trim() === "");
    if (hasEmpty) {
      toast.error("Please fill in all required fields.");
      return false;
    }

    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match.");
      return false;
    }

    if (data.role === "Driver") {
      if (!data.trailerType || String(data.trailerType).trim() === "") {
        toast.error("Please fill in all required fields.");
        return false;
      }
      if (sigCanvas.current?.isEmpty?.() === true) {
        toast.error("Please provide a signature.");
        return false;
      }
    }

    return true;
  };

  const validateEdit = () => {
    const requiredEdit = ["role", "firstName", "lastName", "username", "email"];
    const hasEmpty = requiredEdit.some((k) => !data[k] || String(data[k]).trim() === "");
    if (hasEmpty) {
      toast.error("Please fill in all required fields.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (dialogMode === "create") {
      if (!validateCreate()) return;
      await registerEmployee();
      return;
    }

    if (dialogMode === "edit") {
      if (!validateEdit()) return;
      await updateEmployee();
      return;
    }
  };

  const registerEmployee = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const partnerCompanyId = localStorage.getItem("companyId") || 1;
    const userToken = localStorage.getItem("token");

    let signatureBlob = null;

    if (data.role === "Driver" && sigCanvas.current?.isEmpty?.()) {
      toast.error("Please provide a signature.");
      setIsSubmitting(false);
      return;
    }

    if (data.role === "Driver" && sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataURL = sigCanvas.current.toDataURL("image/png");
      const byteString = atob(dataURL.split(",")[1]);
      const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      signatureBlob = new Blob([uint8Array], { type: mimeString });
    }

    const formDataToSend = new FormData();
    formDataToSend.append("email", data.email);
    formDataToSend.append("user_role_id", String(getUserRoleId(data.role)));
    formDataToSend.append("first_name", data.firstName);
    formDataToSend.append("last_name", data.lastName);
    formDataToSend.append("phone", data.phone);
    formDataToSend.append("username", data.username);
    formDataToSend.append("password", data.password);
    formDataToSend.append("c_password", data.confirmPassword);

    if (data.role === "Driver") {
      formDataToSend.append("partner_company_id", String(partnerCompanyId));
      formDataToSend.append("trailer_type", data.trailerType);
      if (signatureBlob) {
        formDataToSend.append("signature", signatureBlob, "signature.png");
      }
    }

    const endpoint =
      data.role === "Driver"
        ? `https://new.cajuu.app/api/user-driver`
        : `https://new.cajuu.app/api/users`;

    const headers = {
      "Screen-Name": "/User",
      Authorization: `Bearer ${userToken}`,
    };

    try {
      await axios.post(endpoint, formDataToSend, { headers });

      toast.success("User registered successfully.");
      setIsDialogOpen(false);
      resetForm();

      setCurrentPage(1);
      await fetchEmployees(1);

      // refresh driver map (new driver may have been created)
      await fetchDriversForCompany();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateEmployee = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const userToken = localStorage.getItem("token");
      const headers = {
        "Screen-Name": "/User",
        Authorization: `Bearer ${userToken}`,
      };

      await axios.post(
        `https://new.cajuu.app/api/users/${data.id}`,
        {
          first_name: data.firstName,
          last_name: data.lastName,
          phone: data.phone,
          username: data.username,
          email: data.email,
          user_role_id: getUserRoleId(data.role),
        },
        { headers }
      );

      toast.success("User updated successfully.");
      setIsDialogOpen(false);
      resetForm();

      await fetchEmployees(currentPage);
      await fetchDriversForCompany();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (employee) => {
    setData({
      id: employee.id,
      firstName: employee.first_name || "",
      lastName: employee.last_name || "",
      phone: employee.phone || "",
      username: employee.username || "",
      email: employee.email || "",
      password: "",
      confirmPassword: "",
      role: getUserRoleName(employee.user_role_id),
      trailerType: "",
    });
    setDialogMode("edit");
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      const userToken = localStorage.getItem("token");

      const headers = {
        "Screen-Name": "/User",
        Authorization: `Bearer ${userToken}`,
      };

      await axios.post(
        `https://new.cajuu.app/api/user-is-active/${id}`,
        { is_active: isActive },
        { headers }
      );

      setEmployees((prev) =>
        prev.map((employee) => (employee.id === id ? { ...employee, is_active: isActive } : employee))
      );

      toast.success("User status updated successfully.");
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  // ✅ factoring toggle (updates Driver.is_factoring_enabled)
  const handleToggleFactoring = async (driverId, isEnabled) => {
    if (!driverId) return;
    if (updatingFactoringDriverId) return;

    setUpdatingFactoringDriverId(String(driverId));

    try {
      const userToken = localStorage.getItem("token");
      const headers = {
        "Screen-Name": "/User",
        Authorization: `Bearer ${userToken}`,
        "Content-Type": "application/json",
      };

      const res = await axios.post(
        `${API_ORIGIN}/api/driver-is-factoring-enabled/${driverId}`,
        { is_factoring_enabled: !!isEnabled },
        { headers }
      );

      const updatedEnabledRaw =
        res?.data?.is_factoring_enabled ??
        res?.data?.data?.is_factoring_enabled ??
        res?.data?.data?.driver?.is_factoring_enabled ??
        null;

      const updatedEnabled = Number(updatedEnabledRaw) === 1 || updatedEnabledRaw === true;

      // optimistic update maps
      setDriversById((prev) => {
        const next = { ...prev };
        const d = next[String(driverId)];
        if (d) {
          next[String(driverId)] = { ...d, is_factoring_enabled: updatedEnabled ? 1 : 0 };
        }
        return next;
      });

      setDriversByUserId((prev) => {
        // find the driver object in any map and update by user_id
        const next = { ...prev };
        const existing = Object.values(next).find((d) => String(d?.id) === String(driverId));
        const userId = existing?.user_id ?? existing?.user?.id ?? null;

        if (userId) {
          const d = next[String(userId)];
          if (d) {
            next[String(userId)] = { ...d, is_factoring_enabled: updatedEnabled ? 1 : 0 };
          }
        } else {
          // fallback: try from driversById (latest state)
          const d2 = driversById[String(driverId)];
          const uid2 = d2?.user_id ?? d2?.user?.id ?? null;
          if (uid2) {
            const d = next[String(uid2)];
            if (d) {
              next[String(uid2)] = { ...d, is_factoring_enabled: updatedEnabled ? 1 : 0 };
            }
          }
        }

        return next;
      });

      toast.success("Factoring status updated successfully.");
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setUpdatingFactoringDriverId(null);
    }
  };

  const handleResetPassword = async (email) => {
    try {
      const userToken = localStorage.getItem("token");

      const headers = {
        "Screen-Name": "/User",
        Authorization: `Bearer ${userToken}`,
      };

      await axios.post(
        `https://new.cajuu.app/api/password/email`,
        { email },
        { headers }
      );

      toast.success("Password reset email sent successfully.");
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleCreateInvite = async (event) => {
    event.preventDefault();
    try {
      const userToken = localStorage.getItem("token");

      const payload = {
        user_role_id: inviteData.role,
        email: inviteData.email,
      };

      await axios.post(
        `https://new.cajuu.app/api/partnercompany/invite_staff`,
        payload,
        {
          headers: {
            "Screen-Name": "/PartnerCompany",
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      toast.success("Invite sent successfully.");
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleInvite = (e) => {
    if (!inviteData.email || !inviteData.role) {
      toast.error("Please fill in all required fields.");
      return;
    }
    handleCreateInvite(e);
    setIsInviteDialogOpen(false);
    setInviteData({ email: "", role: "" });
  };

  // ✅ pagination UI
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setPerPage(20);
    setSortBy("first_name");
    setSortDirection("asc");
    setCurrentPage(1);
    toast.info("Filters clean");
  };

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection title="EMPLOYEES" description="Employees overview" />
        </GroupedContainer>

        {/* Filters (same layout as Shippers) */}
        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex justify-between items-center mb-4 gap-3">
            <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <TbFilter className="text-amber-500 w-5 h-5" />
              Filters
            </h2>

            <div className="flex items-center gap-2">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
              >
                <TbX className="w-4 h-4" /> Clear Filters
              </button>

              {/* Add Employee */}
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger
                  className="text-sm px-4 py-2 bg-amber-600 text-black rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
                  onClick={() => {
                    setDialogMode("create");
                    setIsDialogOpen(true);
                    resetForm();
                  }}
                >
                  <TbPlus className="w-4 h-4" />
                  Add Employee
                </DialogTrigger>

                <DialogContent className="h-[90vh] overflow-y-auto sm:h-auto sm:max-h-[90vh]">
                  <div>
                    <DialogTitle>{dialogMode === "create" ? "Add Employee" : "Edit Employee"}</DialogTitle>

                    <div className="mt-4 space-y-6">
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-amber-500">Basic Information</h3>

                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Role <span className="text-red-500">*</span>
                          </label>
                          <select
                            id="role"
                            name="role"
                            value={data.role}
                            onChange={(e) => {
                              const newRole = e.target.value;
                              setData((prev) => ({
                                ...prev,
                                role: newRole,
                                ...(newRole !== "Driver" ? { trailerType: "" } : {}),
                              }));
                              if (newRole !== "Driver") {
                                try {
                                  clearSignature();
                                } catch (err) {}
                              }
                            }}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                          >
                            <option value="" className="bg-[#1c1b19]">Select a role</option>
                            <option value="Admin" className="bg-[#1c1b19]">Admin</option>
                            <option value="Driver" className="bg-[#1c1b19]">Driver</option>
                            <option value="Customer" className="bg-[#1c1b19]">Customer</option>
                            <option value="Carrier Company Owner" className="bg-[#1c1b19]">Carrier Company Owner</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={data.firstName}
                              onChange={(e) => setData({ ...data, firstName: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={data.lastName}
                              onChange={(e) => setData({ ...data, lastName: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Phone <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="tel"
                              value={data.phone}
                              onChange={(e) => setData({ ...data, phone: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Username <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={data.username}
                              onChange={(e) => setData({ ...data, username: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={data.email}
                            onChange={(e) => setData({ ...data, email: e.target.value })}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>

                        {dialogMode === "edit" && (
                          <SecondaryButton
                            onClick={() => handleResetPassword(data.email)}
                            className="text-white border-white hover:bg-white/10"
                          >
                            Reset Password
                          </SecondaryButton>
                        )}
                      </div>

                      {dialogMode === "create" && (
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold text-blue-500">Credentials</h3>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-400 mb-1">
                                Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                value={data.password}
                                onChange={(e) => setData({ ...data, password: e.target.value })}
                                className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-400 mb-1">
                                Confirm Password <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="password"
                                value={data.confirmPassword}
                                onChange={(e) => setData({ ...data, confirmPassword: e.target.value })}
                                className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {dialogMode === "create" && data.role === "Driver" && (
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold text-amber-500">Driver Details</h3>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Trailer Type <span className="text-red-500">*</span>
                            </label>
                            <select
                              id="trailer_type"
                              name="trailer_type"
                              value={data.trailerType}
                              onChange={(e) => setData({ ...data, trailerType: e.target.value })}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            >
                              <option value="" className="bg-[#1c1b19]">Select a trailer type</option>
                              <option value="open" className="bg-[#1c1b19]">Open</option>
                              <option value="enclosed" className="bg-[#1c1b19]">Enclosed</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">
                              Signature <span className="text-red-500">*</span>
                            </label>

                            <SignatureCanvas
                              ref={sigCanvas}
                              penColor="white"
                              canvasProps={{
                                height: 150,
                                className: "w-full rounded-lg border border-zinc-600 bg-transparent",
                              }}
                            />

                            <div className="flex justify-end gap-3 mt-4">
                              <button
                                onClick={clearSignature}
                                type="button"
                                className="px-4 py-2 rounded-lg font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
                              >
                                Clear
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end gap-3 pt-2">
                        <SecondaryButton
                          variant="outline"
                          onClick={() => {
                            setIsDialogOpen(false);
                            setDialogMode("create");
                            resetForm();
                          }}
                          className="text-white border-white hover:bg-white/10"
                        >
                          Cancel
                        </SecondaryButton>

                        {dialogMode === "create" ? (
                          <PrimaryButton
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="bg-amber-600 text-black hover:bg-amber-700"
                          >
                            {isSubmitting ? "Saving..." : "Add"}
                          </PrimaryButton>
                        ) : (
                          <ConfirmationDialog
                            triggerButton={
                              <PrimaryButton
                                disabled={isSubmitting}
                                className="bg-amber-600 text-black hover:bg-amber-700"
                              >
                                {isSubmitting ? "Saving..." : "Update"}
                              </PrimaryButton>
                            }
                            confirmText="Update"
                            confirmAction={() => handleSubmit()}
                          >
                            <DialogTitle>Update this employee?</DialogTitle>
                            <p className="text-sm text-white leading-relaxed">
                              You're about to update employee{" "}
                              <span className="font-semibold">
                                {data.firstName} {data.lastName}
                              </span>
                              . Would you like to continue?
                            </p>
                          </ConfirmationDialog>
                        )}
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Invite Employee */}
              <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
                <DialogTrigger
                  className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  onClick={() => {
                    setIsInviteDialogOpen(true);
                    setInviteData({ email: "", role: "" });
                  }}
                >
                  <TbPlus className="w-4 h-4" />
                  Invite
                </DialogTrigger>

                <DialogContent className="w-full sm:max-w-md">
                  <div>
                    <DialogTitle>Invite Employee</DialogTitle>

                    <div className="mt-4 space-y-6">
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-amber-500">Invite Details</h3>

                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={inviteData.email}
                            onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Role <span className="text-red-500">*</span>
                          </label>
                          <select
                            id="invite_role"
                            name="invite_role"
                            value={inviteData.role}
                            onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          >
                            <option value="" className="bg-[#1c1b19]">Select a role</option>
                            <option value="1" className="bg-[#1c1b19]">Admin</option>
                            <option value="2" className="bg-[#1c1b19]">Driver</option>
                            <option value="7" className="bg-[#1c1b19]">Dispatcher</option>
                            <option value="9" className="bg-[#1c1b19]">Accountant</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                        <SecondaryButton
                          variant="outline"
                          onClick={() => {
                            setIsInviteDialogOpen(false);
                            setInviteData({ email: "", role: "" });
                          }}
                          className="text-white border-white hover:bg-white/10"
                        >
                          Cancel
                        </SecondaryButton>

                        <PrimaryButton
                          onClick={(e) => handleInvite(e)}
                          className="bg-amber-600 text-black hover:bg-amber-700"
                        >
                          Invite
                        </PrimaryButton>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* 4 controls like Shippers */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Universal Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbSearch className="text-white text-lg" />
              </div>
              <input
                type="text"
                placeholder="Search name, email, phone, username..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                  title="Clear search"
                >
                  <TbX className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Per Page */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
              <select
                value={String(perPage)}
                onChange={(e) => {
                  setPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all
                           appearance-none"
              >
                <option value="10" className="bg-[#1c1b19]">10 / page</option>
                <option value="20" className="bg-[#1c1b19]">20 / page</option>
                <option value="50" className="bg-[#1c1b19]">50 / page</option>
                <option value="100" className="bg-[#1c1b19]">100 / page</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            {/* Order By */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbFilter className="text-amber-500 text-lg" />
              </div>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all
                           appearance-none"
              >
                <option value="first_name" className="bg-[#1c1b19]">Order by: First Name</option>
                <option value="last_name" className="bg-[#1c1b19]">Order by: Last Name</option>
                <option value="email" className="bg-[#1c1b19]">Order by: Email</option>
                <option value="phone" className="bg-[#1c1b19]">Order by: Phone</option>
                <option value="username" className="bg-[#1c1b19]">Order by: Username</option>
                <option value="user_role_id" className="bg-[#1c1b19]">Order by: Role</option>
                <option value="is_active" className="bg-[#1c1b19]">Order by: Active</option>
                <option value="created_at" className="bg-[#1c1b19]">Order by: Created</option>
                <option value="updated_at" className="bg-[#1c1b19]">Order by: Updated</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            {/* Direction */}
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
              <select
                value={sortDirection}
                onChange={(e) => {
                  setSortDirection(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all
                           appearance-none"
              >
                <option value="asc" className="bg-[#1c1b19]">Direction: ASC</option>
                <option value="desc" className="bg-[#1c1b19]">Direction: DESC</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>
          </div>

          {/* small helper */}
          <div className="mt-3 text-xs text-white/70 flex items-center gap-2">
            {loadingDrivers ? (
              <>
                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-amber-500"></span>
                Loading drivers (for factoring status)...
              </>
            ) : (
              <>
                <TbInfoCircle className="w-4 h-4 text-white/60" />
                Factoring switch appears only for Driver (2) and Carrier Company Owner (4).
              </>
            )}
          </div>
        </div>

        {/* Table (same style as Shippers) */}
        <div className="mt-6 overflow-x-auto rounded-xl border border-zinc-600">
          <table className="min-w-full divide-y divide-zinc-600">
            <thead className="bg-[#1c1b19]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Role</th>

                {/* ✅ NEW: Factoring column */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <span className="inline-flex items-center gap-2">
                    Factoring
                    <span className="text-white/60" title={FACTORING_TOOLTIP}>
                      <TbInfoCircle className="w-4 h-4" />
                    </span>
                  </span>
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Active</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                    </div>
                    <p className="mt-2 text-gray-400">Loading employees...</p>
                  </td>
                </tr>
              ) : employees.length > 0 ? (
                employees.map((employee) => {
                  const profilePicture = employee.profile_picture_path
                    ? `https://new.cajuu.app/storage/${employee.profile_picture_path}`
                    : fallbackImage;

                  const roleId = Number(employee.user_role_id || 0);
                  const isFactoringEligible = roleId === 2 || roleId === 4;

                  const driverForUser = driversByUserId[String(employee.id)] || null;
                  const driverId = driverForUser?.id ? String(driverForUser.id) : null;
                  const factoringEnabled = Number(driverForUser?.is_factoring_enabled || 0) === 1;

                  const isUpdatingThis = updatingFactoringDriverId && driverId && String(updatingFactoringDriverId) === String(driverId);

                  return (
                    <tr key={employee.id} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                        <div className="flex items-center">
                          <img
                            src={profilePicture || "/placeholder.svg"}
                            alt="Profile"
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = fallbackImage;
                            }}
                          />
                          <span className="ml-3">
                            {employee.first_name} {employee.last_name}
                          </span>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        <span className="block max-w-[320px] truncate" title={employee.email || ""}>
                          {employee.email || "-"}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {getUserRoleName(employee.user_role_id)}
                      </td>

                      {/* ✅ NEW: Factoring switch */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {isFactoringEligible ? (
                          <div className="relative inline-flex items-center group">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={!!factoringEnabled}
                                disabled={!driverId || isUpdatingThis}
                                onChange={(e) => {
                                  if (!driverId) {
                                    toast.error("No Driver record found for this user.");
                                    return;
                                  }
                                  handleToggleFactoring(driverId, e.target.checked);
                                }}
                              />
                              <div
                                className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-600 ${
                                  !driverId || isUpdatingThis ? "opacity-60 cursor-not-allowed" : ""
                                }`}
                              ></div>
                            </label>

                            {/* tooltip */}
                            <div
                              className="pointer-events-none absolute left-0 top-full mt-2 w-[320px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20"
                              style={{ maxWidth: "min(320px, 70vw)" }}
                            >
                              <div className="bg-black/90 border border-zinc-700 text-white text-xs rounded-lg p-3 shadow-xl whitespace-pre-line">
                                {FACTORING_TOOLTIP}
                                {!driverId ? (
                                  <div className="mt-2 text-amber-300">
                                    Note: this user has no Driver record yet.
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {isUpdatingThis ? (
                              <span className="ml-3 text-xs text-white/70 inline-flex items-center gap-2">
                                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-500"></span>
                                Saving...
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-white/40">—</span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!employee.is_active}
                            onChange={(e) => handleToggleActive(employee.id, e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-amber-600"></div>
                        </label>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(employee)}
                            className="p-2 text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50 rounded-lg transition-colors"
                            title="View / Edit"
                          >
                            <TbEye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                    <div className="text-gray-400">
                      <TbInfoCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      No employees found matching your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination (same as Shippers) */}
        {paginationMeta.total > 0 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={currentPage === 1}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => paginate(i)}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-white/90"
                            : "text-black border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }
                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing {paginationMeta.from || 0}-{paginationMeta.to || 0} of {paginationMeta.total || 0} employees
            </div>
          </>
        )}
      </div>
    </AuthenticatedLayout>
  );
}